import { AnalyticService } from '@application/analytic/analytic.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { Flex, Stack, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Async } from '@component/async.component';
import { useService } from '@visiba/instantiation';
import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ChunkVariants, CookiePolicyController } from './_cookie_policy.controller';
import { CookiePolicySkeleton } from './_cookie_policy_skeleton';

interface Props {}

export function CookiePolicy(_: Props): JSX.Element {
	const analyticService = useService(AnalyticService);

	useEffect(
		function analyticQuirk_whenComponentMounts() {
			analyticService.trackPageView({ customTitle: 'Cookie-policy' });
		},
		[analyticService],
	);

	return (
		<Routes>
			<Route index element={<Content />} />
			<Route path='/sv' element={<Content explicitLanguage='sv' />} />
			<Route path='/fi' element={<Content explicitLanguage='fi' />} />
			<Route path='*' element={<Content explicitLanguage='en-gb' />} />
		</Routes>
	);
}

interface ContentProps {
	explicitLanguage?: I18nAndL10n.AvailableVisibaLocales;
}

function Content({ explicitLanguage }: ContentProps): JSX.Element {
	const cookiePolicyController = useService(CookiePolicyController);
	const analyticService = useService(AnalyticService);

	useEffect(
		function analyticQuirk_whenComponentMounts() {
			analyticService.trackPageView({ customTitle: `Cookie-policy-${explicitLanguage}` });
		},
		[analyticService, explicitLanguage],
	);

	useEffect(() => {
		cookiePolicyController.load(explicitLanguage);
	});

	return (
		<Flex
			gap={tokens.spacing.x3}
			breakpointSmallAndUp={{
				gap: tokens.spacing.x4,
			}}
		>
			<Async derive={cookiePolicyController.presentation} fallback={<CookiePolicySkeleton />} catch={null}>
				{(data) => (
					<>
						{data.chunks.map((chunk) => (
							<TextChunk key={chunk.id} variant={chunk} />
						))}
					</>
				)}
			</Async>
		</Flex>
	);
}

function TextChunk({ variant }: { variant: ChunkVariants }): JSX.Element {
	switch (variant.type) {
		case 'title':
			return <Text.h1 variant='headingXLarge'>{variant.header}</Text.h1>;
		case 'perceived':
			return <Text.p variant='bodyLargeRegular'>{variant.body}</Text.p>;
		case 'section':
			return (
				<Stack gap={tokens.spacing.x1}>
					<Text.h2 variant='headingSmall'>{variant.header}</Text.h2>
					<Text.p variant='bodyRegular'>{variant.body}</Text.p>
				</Stack>
			);
		case 'table':
			return (
				<>
					<Text.p variant='bodyRegular'>{variant.body}</Text.p>

					<Text.h2 variant='headingSmall'>{variant.head[0]}</Text.h2>
					<Flex as='ul' gap={tokens.spacing.x3}>
						{variant.row.map((rowItem, index) => (
							<Flex as='li' key={index} gap={tokens.spacing.x1}>
								<Text.h3 variant='headingXSmall'>{rowItem[0]}</Text.h3>
								<Text.p variant='bodyRegular'>{rowItem[1]}</Text.p>
							</Flex>
						))}
					</Flex>
				</>
			);
	}
}
